import { useState, useEffect } from 'react'
import { apiService, type AuditLog, type ApiError } from '../services/api'

interface UseAuditHistoryProps {
  userId: string
  autoLoad?: boolean
}

interface UseAuditHistoryReturn {
  auditLogs: AuditLog[]
  isLoading: boolean
  error: string | null
  hasMore: boolean
  currentPage: number
  totalLogs: number
  loadMore: () => Promise<void>
  refresh: () => Promise<void>
  clearError: () => void
}

export const useAuditHistory = ({ 
  userId, 
  autoLoad = true 
}: UseAuditHistoryProps): UseAuditHistoryReturn => {
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [hasMore, setHasMore] = useState(true)
  const [totalLogs, setTotalLogs] = useState(0)
  const [isInitialized, setIsInitialized] = useState(false)

  const limit = 15 // Logs per page

  const fetchAuditHistory = async (page: number, append: boolean = false) => {
    if (!userId) return

    setIsLoading(true)
    setError(null)

    try {
      const response = await apiService.getUserAuditHistory(userId, page, limit)
            
      const newLogs = response.data || []
      
      if (append) {
        setAuditLogs(prev => [...prev, ...newLogs])
      } else {
        setAuditLogs(newLogs)
      }

      setTotalLogs(response.total || 0)
      setCurrentPage(page)
      
      // Check if there are more logs to load
      const totalPages = Math.ceil((response.total || 0) / limit)
      setHasMore(page < totalPages)
      
    } catch (err) {
      const apiError = err as ApiError
      setError(Array.isArray(apiError.message) ? apiError.message.join(', ') : apiError.message || 'Failed to load audit history')
      console.error('Error loading audit history:', apiError)
    } finally {
      setIsLoading(false)
      if (!isInitialized) {
        setIsInitialized(true)
      }
    }
  }

  // Load initial data
  useEffect(() => {
    if (autoLoad && userId && !isInitialized) {
      fetchAuditHistory(1, false)
    }
  }, [userId, autoLoad, isInitialized])

  const loadMore = async () => {
    if (!hasMore || isLoading) return
    await fetchAuditHistory(currentPage + 1, true)
  }

  const refresh = async () => {
    setCurrentPage(1)
    setHasMore(true)
    await fetchAuditHistory(1, false)
  }

  const clearError = () => {
    setError(null)
  }

  return {
    auditLogs,
    isLoading,
    error,
    hasMore,
    currentPage,
    totalLogs,
    loadMore,
    refresh,
    clearError
  }
}